/* eslint-disable max-statements */
import { useMantineTheme } from '@mantine/core';
import { IconBell, IconCancel, IconFlaskFilled, IconRosetteDiscountCheckFilled } from '@tabler/icons-react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import { TSDListItem } from '@e-origin/contracts';
import {
  Customer,
  EnvironmentCountries,
  EORIGIN_DATE_FORMAT_WITH_TIME,
  Template,
  TSDBusinessValidationType,
  TSDStatus,
  TSDTestResponseType,
  TSDType,
} from '@e-origin/shared';

import { copyIcon, customsSyncIcon, deleteIcon, finalizeIcon, IconFactory, invalidatedIcon, revokeIcon, sendIcon, viewIcon } from '@assets';
import { IndicatorMark, TableActions, TableColumnProps, TableNotificationIndicator } from '@components';
import { TABLE_MENU_THRESHOLD } from '@constants';
import { useAmendTSD, useCancelAmendment, useCustomsSyncTSD, useDeleteTSD, useSendOneTSD, useTriggerTsdNotification } from '@hooks/tsd';
import { useEnableTsdSimulatorMode, useSetTsdImmediateResponse } from '@hooks/tsd/simulator';
import { useSettings } from '@hooks/useSettings';
import { TSDStatusIndicator } from '../TSDStatusIndicator';
import { TsdSimulatorActionsMenu, TSDSimulatorModalManager } from './CustomsSimulator';
import { TSDModalManager } from './TSDModalManager';

export const useTSDTableColumns = ({ pagination, tsdView }) => {
  const navigate = useNavigate();
  const { data: settings } = useSettings();
  const theme = useMantineTheme();

  const deleteTSDMutation = useDeleteTSD();
  const amendTSDMutation = useAmendTSD();
  const cancelAmendmentMutation = useCancelAmendment();
  const sendOneMutation = useSendOneTSD();
  const triggerTsdNotification = useTriggerTsdNotification();
  const customsSyncMutation = useCustomsSyncTSD();
  const enableTsdSimulatorMode = useEnableTsdSimulatorMode();
  const setTsdImmediateResponse = useSetTsdImmediateResponse();

  const canUseTsdSimulatorFeature = settings?.isProd === false && settings?.country !== EnvironmentCountries.NL;

  const actions = (row: TSDListItem) => {
    const prelodgedPNAllowed =
      (row.customsState?.businessValidationType === TSDBusinessValidationType.PRELODGED && row.generalInfo.status === TSDStatus.REJECTED) ||
      [TSDStatus.PRELODGED, TSDStatus.NOT_LINKED].includes(row.generalInfo.status);

    const amendmentAllowed =
      [TSDType.COMBINED, TSDType.PRELODGED].includes(tsdView) &&
      [TSDStatus.ACCEPTED, TSDStatus.PRELODGED, TSDStatus.REJECTED].includes(row.generalInfo.status) &&
      !row.generalInfo.isDeconsolidated &&
      row.generalInfo.isAmended;

    const cancelAmendmentAllowed = row.generalInfo.isAmended && row.generalInfo.status !== TSDStatus.SENDING;
    const isInitialDeclarationRejected = row.customsState?.customsDeclarationRejection?.businessRejectionType === TSDBusinessValidationType.ACCEPTED_AMENDMENT;

    const canSendTsd = [TSDStatus.NOT_SENT, TSDStatus.REJECTED].includes(row.generalInfo.status) && !prelodgedPNAllowed && !isInitialDeclarationRejected;

    const sendTsdToSimulator = async (row: TSDListItem, responseType: 'acknowledge' | 'rejected') => {
      await setTsdImmediateResponse.mutateAsync({
        params: {
          tsdId: row._id,
        },
        body: {
          responseType,
        },
      });
      sendOneMutation.mutate(row._id);
    };

    const sendToSimulatorBtn = (tsd: TSDListItem) => {
      const simulatorOptions = [
        {
          iconComponent: <IconRosetteDiscountCheckFilled color={theme.colors.green[6]} />,
          text: 'Acknowledge',
          onClick: () => sendTsdToSimulator(tsd, 'acknowledge'),
        },
        {
          iconComponent: <IconCancel color={theme.colors.red[6]} />,
          text: 'Technical Rejection',
          responseType: 'rejected' as const,
          onClick: () => sendTsdToSimulator(tsd, 'rejected'),
        },
      ];

      return {
        iconComponent: (
          <IndicatorMark>
            <img src={IconFactory.send} alt="Send to simulator" />
          </IndicatorMark>
        ),
        multi: true,
        options: simulatorOptions,
        text: 'Send to simulator',
      };
    };

    return [
      {
        if: canUseTsdSimulatorFeature && !row.useCustomsSimulator && row.generalInfo.status === TSDStatus.NOT_SENT,
        iconComponent: <IconFlaskFilled size={20} color={theme.colors.blue[6]} />,
        text: 'Enable Simulator Mode',
        onClick: () => {
          enableTsdSimulatorMode.mutate({
            params: {
              tsdId: row._id,
            },
          });
        },
        disableAfterClick: false,
        preventDefaultClick: true,
      },
      {
        icon: viewIcon,
        text: 'View details',
        onClick: () => {
          navigate(`/tsd/${row._id}`);
        },
        preventDefaultClick: true,
      },
      {
        if: [TSDStatus.NOT_SENT, TSDStatus.REJECTED].includes(row.generalInfo.status) && !row.generalInfo.wasAcceptedByCustoms,
        icon: deleteIcon,
        text: 'Delete temporary storage',
        onClick: () => TSDModalManager.openDeleteTSD(row._id, (tsdId) => deleteTSDMutation.mutate(tsdId)),
        disableAfterClick: false,
        preventDefaultClick: true,
      },
      {
        if: canUseTsdSimulatorFeature && row.useCustomsSimulator === true && canSendTsd,
        ...sendToSimulatorBtn(row),
      },
      {
        if: !row.useCustomsSimulator && canSendTsd,
        icon: sendIcon,
        text: 'Send',
        onClick: () => {
          sendOneMutation.mutate(row._id);
        },
        disableAfterClick: false,
        preventDefaultClick: true,
      },
      {
        if: prelodgedPNAllowed && !row.generalInfo.isAmended,
        icon: finalizeIcon,
        text: 'Send presentation notification',
        onClick: () => TSDModalManager.openSendPresentationNotification(row._id),
        disableAfterClick: false,
        preventDefaultClick: true,
      },
      // {
      // if: [TSDStatus.PRESENTED, TSDStatus.LINKED, TSDStatus.NOT_LINKED].includes(row.generalInfo.status),
      //   icon: revokeIcon,
      //   text: 'Revoke presentation notification',
      //   onClick: () => TSDModalManager.openRevokePresentationNotification(row._id),
      //   disableAfterClick: false,
      //   preventDefaultClick: true,
      // },
      {
        if: amendmentAllowed,
        icon: invalidatedIcon,
        text: 'Amend temporary storage',
        onClick: () => {
          amendTSDMutation.mutate(row._id);
        },
        preventDefaultClick: true,
        disableAfterClick: false,
      },
      {
        if: cancelAmendmentAllowed,
        icon: revokeIcon,
        text: 'Cancel amendment',
        onClick: () => {
          cancelAmendmentMutation.mutate(row._id);
        },
        preventDefaultClick: true,
        disableAfterClick: false,
      },
      {
        if: row.useCustomsSimulator !== true && ![TSDStatus.NOT_SENT, TSDStatus.SENDING].includes(row.generalInfo.status),
        icon: customsSyncIcon,
        text: 'Customs sync',
        onClick: () => {
          customsSyncMutation.mutate(row._id);
        },
        disableAfterClick: false,
        preventDefaultClick: true,
      },
    ];
  };

  const secondaryActions = (row: TSDListItem) => {
    const amendmendAllowed =
      [TSDType.COMBINED, TSDType.PRELODGED].includes(tsdView) &&
      [TSDStatus.ACCEPTED, TSDStatus.PRELODGED].includes(row.generalInfo.status) &&
      !row.generalInfo.isDeconsolidated &&
      !row.generalInfo.isAmended;

    return [
      {
        if: [TSDType.COMBINED, TSDType.PRELODGED].includes(tsdView),
        icon: copyIcon,
        text: 'Duplicate temporary storage',
        onClick: () => TSDModalManager.openDuplicate(row),
        preventDefaultClick: true,
      },
      {
        if: amendmendAllowed,
        icon: invalidatedIcon,
        text: 'Amend temporary storage',
        onClick: () => TSDModalManager.openAmend(row, () => navigate(`/tsd/${row._id}`)),
        preventDefaultClick: true,
        disableAfterClick: false,
      },
    ];
  };

  const openTsdSimulatorModal = (row: TSDListItem, messageType: TSDTestResponseType) => {
    const onSubmit = (submittedData?: Record<string, unknown>) => {
      triggerTsdNotification.mutate({
        tsdId: row._id,
        submittedData,
        messageType,
      });
    };

    switch (messageType) {
      case TSDTestResponseType.ACCEPTED: {
        TSDSimulatorModalManager.openAccepted({ tsd: row, onSubmit });
        return;
      }

      case TSDTestResponseType.TSD_ACTIVATION: {
        TSDSimulatorModalManager.openActivation({ tsd: row, onSubmit });
        return;
      }

      case TSDTestResponseType.PN_LINKING_RESULT: {
        TSDSimulatorModalManager.openPNLinkingResult({ tsd: row, onSubmit });
        return;
      }

      case TSDTestResponseType.WRITE_OFF: {
        TSDSimulatorModalManager.openWriteOff({ tsd: row, onSubmit });
        return;
      }

      case TSDTestResponseType.STATUS_UPDATE_NOTIFICATION: {
        TSDSimulatorModalManager.openStatusUpdate({ tsd: row, onSubmit });
        return;
      }

      default:
        triggerTsdNotification.mutate({
          tsdId: row._id,
          messageType,
        });
    }
  };

  const buildColumns = (): TableColumnProps<TSDListItem>[] => [
    {
      name: <IconBell />,
      cell: (row) => (
        <TableNotificationIndicator notificationsPending={row.customsState?.notifications?.filter((notification) => notification.read === false).length || 0} />
      ),
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: true,
      cell: (row) => (
        <div className="whitespace-nowrap overflow-hidden overflow-ellipsis">
          <TSDStatusIndicator status={row.generalInfo.status} />
        </div>
      ),
    },
    {
      name: 'Temporary Storage ID',
      selector: 'counter',
      cell: (row) => row.counter,
      sortable: true,
      width: '170px',
    },
    {
      name: 'Name',
      selector: 'name',
      cell: (row) => row.name,
      sortable: true,
    },
    {
      name: 'LRN',
      selector: 'messageInfo.LRN',
      cell: (row) => row.messageInfo?.LRN || '',
      sortable: true,
    },
    {
      name: 'CRN',
      selector: 'messageInfo.CRN',
      cell: (row) => row.messageInfo?.CRN || '',
      sortable: true,
    },
    {
      name: 'MRN',
      selector: 'messageInfo.MRN',
      cell: (row) => row.messageInfo?.MRN || '',
      sortable: true,
    },
    {
      name: 'Customer',
      cell: (row) => (row.customer as unknown as Customer)?.name,
    },
    {
      name: 'Template',
      cell: (row) => (row.template as unknown as Template)?.name,
    },
    {
      name: 'Last update',
      selector: 'updatedAt',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      sortable: true,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row, rowIdx) => (
        <TableActions
          actions={actions(row)}
          secondaryActions={secondaryActions(row)}
          disableAfterClick={true}
          moveMenuUpwards={pagination.size - rowIdx <= TABLE_MENU_THRESHOLD}
          data-pw={rowIdx}
        >
          {canUseTsdSimulatorFeature && row.useCustomsSimulator === true && (
            <TsdSimulatorActionsMenu tsd={row} tsdView={tsdView} onSelect={(messageType) => openTsdSimulatorModal(row, messageType)} />
          )}
        </TableActions>
      ),
    },
  ];

  return buildColumns();
};
